import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input,
  ChangeDetectorRef, Inject, OnInit, OnDestroy, OnChanges, SimpleChanges
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import isEmpty from 'lodash-es/isEmpty';
import { Subscription, tap } from 'rxjs';

import {
  BILLING_FORM_INPUTS, BOOKING_FORM_INPUTS, CONTROL, INVALID, PERSON_FORM_INPUTS
} from '@booking/constants/forms.constants';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { User } from '@app/modules/core/interfaces/user';
import { CONTACT_INFO_TRANSLATE_ROOT } from '@forms/contact-form/constants/contact-translate.constants';
import { CONTACT_FORM_PROPS } from '@forms/contact-form/constants/contact-form-props.constants';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import {
  DataActionCardInfoType, DataActionCardStyle
} from '@design-system/components/templates/data-action-card/entities/data-action-card.types';
import { 
  TypographyColor, 
  TypographyFontFamily, 
  TypographyFontSize 
} from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-contact-section',
  templateUrl: './contact-section.component.html',
  styleUrls: ['./contact-section.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ContactSectionComponent implements OnChanges, OnInit, OnDestroy {

  @Input() isSubmitted: boolean = false;
  @Input() isMobile: boolean;
  @Input() user: User;
  @Input() form: UntypedFormGroup;

  public showContactInfo: boolean = false;
  public showContactInfoModal: boolean = false;
  public emailControl: string = BOOKING_FORM_INPUTS.EMAIL;
  public phoneControl: string = BOOKING_FORM_INPUTS.PHONE;

  private _email: string;
  private _phone: string;

  public readonly icons = Icon;

  public readonly dataActionCardInfoType = DataActionCardInfoType;
  public readonly dataActionCardStyle = DataActionCardStyle;
  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;
  public readonly fontFamily = TypographyFontFamily;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private domManipulateService: DomManipulateService,
    private changeDetector: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.[CONTACT_FORM_PROPS.FORM]) {
      if (this.form.value[this.emailControl]) this.setEmail(this.form.value[this.emailControl]);
      if (this.form.value[this.phoneControl]) this.setPhone(this.form.value[this.phoneControl]);
    }
  }

  ngOnInit(): void {
    if (this.form?.get(this.emailControl)) {
      this.subscriptions.add(
        this.form.get(this.emailControl).valueChanges.pipe(
          tap(value => {
            this.setEmail(value);
          })
        ).subscribe()
      );
    }
    if (this.form?.get(this.phoneControl)) {
      this.form.get(this.phoneControl).valueChanges.pipe(
        tap(value => {
          this.setPhone(value);
        })
      ).subscribe()
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get email(): string {
    return this._email;
  }

  get phone(): string {
    return this._phone;
  }

  get withoutPhoneInfo(): boolean {
    return !this.user?.is_phone_confirmed && !!isEmpty(this.user?.phone);
  }

  get title(): string {
    return this.withoutPhoneInfo ? `${CONTACT_INFO_TRANSLATE_ROOT}.CONTACT_TITLE` :
      `${CONTACT_INFO_TRANSLATE_ROOT}.CONTACT_DESCRIPTION_EMAIL`;
  }

  get description(): string {
    return this.withoutPhoneInfo ? `${CONTACT_INFO_TRANSLATE_ROOT}.CONTACT_DESCRIPTION_SUB_ALTERNATIVE` :
      `${CONTACT_INFO_TRANSLATE_ROOT}.CONTACT_DESCRIPTION_SUB`;
  }

  get formDescription(): string {
    return this.withoutPhoneInfo ? `${CONTACT_INFO_TRANSLATE_ROOT}.CONTACT_DESCRIPTION_FULL` :
      `${CONTACT_INFO_TRANSLATE_ROOT}.CONTACT_DESCRIPTION_EMAIL`;
  }

  get errorInContacts(): boolean {
    const emailFormControl = this.form?.get(this.emailControl);
    const phoneFormControl = this.form?.get(this.phoneControl);
    const isInvalidEmail = !!isEmpty(this.email) || emailFormControl.status === INVALID;
    const isInvalidPhone = !!isEmpty(this.phone) || phoneFormControl.status === INVALID;
    const isInvalidForm = this.isSubmitted && ((isInvalidEmail) || (isInvalidPhone));
    return isInvalidForm;
  }

  get errorMessage(): string {
    const defaultError = 'BOOKING_MODULE.ERRORS.MANDATORY_CONTACT';
    if (this.errorInContacts) {
      const emailFormControl = this.form?.get(this.emailControl);
      const phoneFormControl = this.form?.get(this.phoneControl);
      const isInvalidEmail = !!isEmpty(this.email) || emailFormControl.status === INVALID;
      const isInvalidPhone = !!isEmpty(this.phone) || phoneFormControl.status === INVALID;
      if (isInvalidEmail && isInvalidPhone) {
        return defaultError;
      } else if (isInvalidPhone) {
        return 'BOOKING_MODULE.ERRORS.MANDATORY_CONTACT_PHONE';
      } else return 'BOOKING_MODULE.ERRORS.MANDATORY_CONTACT_EMAIL';
    } else return '';
  }

  public open(): void {
    this.toggleContactInfo();
  }

  public close(): void {
    this.toggleContactInfo();
  
    const email = this.email;
    const phone = this.phone;
  
    this.form.patchValue({
      [this.emailControl]: email,
      [this.phoneControl]: phone,
    });
  
    this.setEmail(email);
    this.setPhone(phone);
  }

  public saveContactInfo(info: { email: string, phone: string }): void {
    this.toggleContactInfo();
    this.setData(info.email, info.phone);
  }

  private toggleContactInfo(): void {
    if (this.isMobile) {
      this.showContactInfo = !this.showContactInfo;
      this.domManipulateService.setBodyScroll(!this.showContactInfo);
    } else {
      this.showContactInfoModal = !this.showContactInfoModal;
      this.domManipulateService.setBodyScroll(!this.showContactInfoModal);
    }
  }

  private setData(email: string, phone: string): void {
    if (email) this.setEmail(email);
    if (phone) this.setPhone(phone);
    this.form?.patchValue({
      [this.emailControl]: this.email,
      [this.phoneControl]: this.phone
    });
    this.setByBillingContact();
  }

  private setByBillingContact(): void {
    const formBilling = this.form?.get(BOOKING_FORM_INPUTS.BILLING)?.get(CONTROL);
    if (formBilling) {
      const billingControl: UntypedFormGroup = formBilling.get(BILLING_FORM_INPUTS.BILLING_INFO) as UntypedFormGroup;
      if (billingControl) {
        const emailGroup: UntypedFormGroup = billingControl.get(PERSON_FORM_INPUTS.EMAIL) as UntypedFormGroup;
        const phoneGroup: UntypedFormGroup = billingControl.get(PERSON_FORM_INPUTS.PHONE) as UntypedFormGroup;
        if (isEmpty(phoneGroup.value)) {
          billingControl.patchValue({
            [PERSON_FORM_INPUTS.PHONE]: this.phone
          });
        }
        if (isEmpty(emailGroup.value)) {
          billingControl.patchValue({
            [PERSON_FORM_INPUTS.EMAIL]: this.email
          });
        }
      }
      formBilling.updateValueAndValidity();
    }
  }

  private setEmail(email: string): void {
    this._email = email;
    this.changeDetector.detectChanges();
  }

  private setPhone(phone: string): void {
    this._phone = phone;
    this.changeDetector.detectChanges();
  }
}
